import axios, { type AxiosError } from 'axios';
import { ErrorCode } from '@condo/domain';
import { ErrorTag, getInnerMostError } from '@condo/helpers';
import { toast } from 'sonner';
import i18n from '@/shared/i18n/instance';

const ERROR_TOAST_STYLE = {
    backgroundColor: 'hsl(var(--destructive))',
    color: 'hsl(var(--destructive-foreground))',
    border: '1px solid hsl(var(--destructive))',
} as const;

type ApiErrorTOptions = Record<string, string | number>;

function tApiError(key: string, options?: ApiErrorTOptions): string {
    return String(i18n.t(`apiErrors.${key}`, { ns: 'main', ...options }));
}

function safeServerErrorMessage(): string {
    return tApiError('safeServerError');
}

function safeGenericErrorMessage(): string {
    return tApiError('safeGenericError');
}

/**
 * Heuristic for leaky backend payloads (Drizzle "Failed query", stacks, connection dumps).
 * Prefer status-based rules; this covers mislabeled 4xx or missing status.
 */
const UNSAFE_INTERNAL_ERROR_PATTERN =
    /failed query|select\s+[\s\S]{0,80}from\s|insert\s+into\s|update\s+\w+\s+set\s|delete\s+from\s|params:\s|ECONNREFUSED|ECONNRESET|ENOTFOUND|relation\s+"|syntax error at|drizzle|at\s+\w+\s+\([^)]+\.(?:ts|js|mjs):\d+|stack trace|postgresql|pg_|\bSQLSTATE\b/i;

interface ApiErrorResponse {
    message?: string;
    error?: string;
    details?: string;
    statusText?: string;
    status?: number;
    data?: {
        code?: string;
        context?: Record<string, unknown>;
    };
    debug?: string;
}

interface FrontendSilentErrorCandidate {
    tags?: unknown;
}

interface ErrorToastInfo {
    id: string;
    timestamp: number;
    message: string;
}

interface ApiErrorDetailParts {
    note?: string;
    httpStatus?: number;
    requestLabel?: string;
    errorCode?: string;
    streamId?: string;
    supplemental?: string;
}

interface ExtractedApiError {
    title: string;
    message: string;
    detailParts?: ApiErrorDetailParts;
    actionButton?: { label: string; url: string };
    duration?: number;
}

type AxiosErrorWithConfig = AxiosError & {
    config?: {
        url?: string;
        method?: string;
        skipGlobalErrorToast?: boolean;
    };
};

function isNetworkError(error: unknown): boolean {
    if (!axios.isAxiosError(error)) {
        return error instanceof TypeError && error.message.includes('fetch');
    }

    const code = error.code;
    const message = error.message ?? '';
    return code === 'ECONNABORTED' || code === 'ENOTFOUND' || code === 'ECONNREFUSED' || message.includes('Network Error') || message.includes('timeout');
}

function getRequestLabel(config: AxiosErrorWithConfig['config'] | undefined): string | undefined {
    if (!config?.url) {
        return undefined;
    }
    const method = config.method?.toUpperCase() ?? 'REQUEST';
    return `${method} ${config.url}`;
}

function parseApiErrorResponseData(data: unknown): ApiErrorResponse {
    if (!data || typeof data !== 'object') {
        return {};
    }
    return data as ApiErrorResponse;
}

function getApiResponseMessage(data: ApiErrorResponse, statusText?: string): string {
    const contextMessage = data.data?.context?.['message'];
    if (typeof contextMessage === 'string' && contextMessage.length > 0) {
        return contextMessage;
    }
    if (typeof data.message === 'string' && data.message.length > 0) {
        return data.message;
    }
    if (typeof data.error === 'string' && data.error.length > 0) {
        return data.error;
    }
    if (typeof statusText === 'string' && statusText.length > 0) {
        return statusText;
    }
    return tApiError('unknownOccurred');
}

function isServerErrorStatus(status: number): boolean {
    return status >= 500 && status <= 599;
}

function isUnsafeInternalErrorMessage(message: string | undefined): boolean {
    if (!message || message.length === 0) {
        return false;
    }
    // Long free-form dumps are almost never meant for end users
    if (message.length > 280) {
        return true;
    }
    return UNSAFE_INTERNAL_ERROR_PATTERN.test(message);
}

/**
 * Map API payload text → safe user-facing copy.
 * 5xx and leaky internals never reach the UI; actionable 4xx messages still do.
 */
function toUserFacingErrorMessage(status: number | undefined, originalMessage: string): string {
    if (status != null && isServerErrorStatus(status)) {
        return safeServerErrorMessage();
    }
    if (isUnsafeInternalErrorMessage(originalMessage)) {
        return safeGenericErrorMessage();
    }
    return originalMessage;
}

function toSafeSupplementalDetail(status: number | undefined, supplemental: string | undefined): string | undefined {
    if (!supplemental) {
        return undefined;
    }
    if ((status != null && isServerErrorStatus(status)) || isUnsafeInternalErrorMessage(supplemental)) {
        return undefined;
    }
    return supplemental;
}

function parseAxiosResponseDataForToastSuppression(data: unknown): unknown {
    if (!(data instanceof Blob)) {
        return data;
    }

    return undefined;
}

function shouldSuppressGlobalErrorToast(error: unknown): boolean {
    if (!axios.isAxiosError(error)) {
        return false;
    }

    const responseData = parseAxiosResponseDataForToastSuppression(error.response?.data);
    const innerMostError = getInnerMostError(responseData) as FrontendSilentErrorCandidate;

    return Array.isArray(innerMostError?.tags) && innerMostError.tags.includes(ErrorTag.FRONTEND_SILENT);
}

function getContextualErrorInfo(status: number, originalMessage: string): { title: string; message: string } {
    const safeMessage = toUserFacingErrorMessage(status, originalMessage);

    switch (status) {
        case 401:
            return {
                title: tApiError('authTitle'),
                message: tApiError('authMessage'),
            };
        case 403:
            return {
                title: tApiError('permissionTitle'),
                message: isUnsafeInternalErrorMessage(originalMessage) ? tApiError('permissionMessage') : safeMessage || tApiError('permissionMessage'),
            };
        case 404:
            return {
                title: tApiError('notFoundTitle'),
                message: isUnsafeInternalErrorMessage(originalMessage) ? tApiError('notFoundMessage') : safeMessage || tApiError('notFoundMessage'),
            };
        case 400:
            return {
                title: tApiError('invalidRequestTitle'),
                message: isUnsafeInternalErrorMessage(originalMessage) ? tApiError('invalidRequestMessage') : safeMessage || tApiError('invalidRequestMessage'),
            };
        case 422:
            return {
                title: tApiError('validationTitle'),
                message: isUnsafeInternalErrorMessage(originalMessage) ? tApiError('validationMessage') : safeMessage || tApiError('validationMessage'),
            };
        case 412:
            return {
                title: tApiError('validationTitle'),
                message: isUnsafeInternalErrorMessage(originalMessage) ? tApiError('validationMessage') : safeMessage || tApiError('validationMessage'),
            };
        case 500:
            return {
                title: tApiError('somethingWentWrongTitle'),
                message: safeServerErrorMessage(),
            };
        case 502:
            return {
                title: tApiError('badGatewayTitle'),
                message: safeServerErrorMessage(),
            };
        case 503:
            return {
                title: tApiError('serviceUnavailableTitle'),
                message: tApiError('serviceUnavailableMessage'),
            };
        case 504:
            return {
                title: tApiError('gatewayTimeoutTitle'),
                message: tApiError('gatewayTimeoutMessage'),
            };
        default:
            if (isServerErrorStatus(status)) {
                return {
                    title: tApiError('somethingWentWrongTitle'),
                    message: safeServerErrorMessage(),
                };
            }
            return {
                title: tApiError('requestFailedTitle', { status }),
                message: safeMessage || safeGenericErrorMessage(),
            };
    }
}

/**
 * Class to manage API error handling with toast notifications
 */
class ApiErrorHandler {
    private static instance: ApiErrorHandler;
    private recentErrors: Map<string, ErrorToastInfo> = new Map();
    private readonly ERROR_DEBOUNCE_TIME = 5000;
    private readonly MAX_CONCURRENT_ERRORS = 3;

    private constructor() {}

    static getInstance(): ApiErrorHandler {
        if (!ApiErrorHandler.instance) {
            ApiErrorHandler.instance = new ApiErrorHandler();
        }
        return ApiErrorHandler.instance;
    }

    async handleApiError(error: unknown, serviceName = 'API'): Promise<void> {
        try {
            if (shouldSuppressGlobalErrorToast(error)) {
                if (import.meta.env.DEV) {
                    console.error(`${serviceName} API error:`, error);
                }
                return;
            }

            const errorInfo = await this.extractErrorInfo(error);
            const errorKey = this.generateErrorKey(errorInfo, serviceName);

            if (this.shouldShowError(errorKey, errorInfo.message)) {
                this.showErrorToast(errorInfo, serviceName);
                this.trackError(errorKey, errorInfo.message);
            }

            if (import.meta.env.DEV) {
                console.error(`${serviceName} API error:`, error);
            }
        } catch (handlerError) {
            console.error('Error in API error handler:', handlerError);
            toast.error(tApiError('handlerFallbackTitle'), {
                description: tApiError('handlerFallbackDescription'),
                duration: 5000,
                style: ERROR_TOAST_STYLE,
            });
        }
    }

    clearTrackedErrors(): void {
        this.recentErrors.clear();
    }

    getTrackedErrorCount(): number {
        return this.recentErrors.size;
    }

    private async extractErrorInfo(error: unknown): Promise<ExtractedApiError> {
        if (isNetworkError(error)) {
            const axiosError = axios.isAxiosError(error) ? error : null;
            return {
                title: tApiError('networkTitle'),
                message: tApiError('networkMessage'),
                detailParts: {
                    note: tApiError('noInternet'),
                    requestLabel: getRequestLabel(axiosError?.config),
                    errorCode: axiosError?.code,
                },
            };
        }

        if (axios.isAxiosError(error) && error.response) {
            const response = error.response;
            let data: ApiErrorResponse = {};

            if (response.data instanceof Blob) {
                try {
                    const blobText = await response.data.text();
                    data = JSON.parse(blobText) as ApiErrorResponse;
                } catch (blobError) {
                    if (import.meta.env.DEV) {
                        console.warn('Failed to parse blob response as JSON:', blobError);
                    }
                    data = { message: tApiError('blobParseFailed') };
                }
            } else {
                data = parseApiErrorResponseData(response.data);
            }

            //@todo when more custom error handlers are added, consider refactoring to a more scalable structure (e.g. map of error code to handler function)
            if (data.data?.code === ErrorCode.CandidateAssetPartnerInCandidatePortfolio) {
                const portfolioId = data.data.context?.['candidatePortfolioId'];
                const hasPortfolioId = typeof portfolioId === 'number';
                return {
                    title: tApiError('candidatePartnerTitle'),
                    message: tApiError('candidatePartnerMessage'),
                    actionButton: hasPortfolioId ? { label: tApiError('viewPortfolio'), url: `/sourcing/mfh-portfolios/${portfolioId}` } : undefined,
                    duration: hasPortfolioId ? Infinity : 6000,
                };
            }

            const requestLabel = getRequestLabel(error.config ?? response.config);
            const streamId = response.headers?.['x-stream-id'];
            const streamIdValue = typeof streamId === 'string' ? streamId : undefined;
            const originalMessage = getApiResponseMessage(data, response.statusText);
            const contextInfo = getContextualErrorInfo(response.status, originalMessage);

            const omitTechnicalDetails = response.status === 400 || response.status === 412 || response.status === 422;
            const rawSupplemental = data.details != null ? String(data.details) : undefined;
            const supplemental = toSafeSupplementalDetail(response.status, rawSupplemental);

            // 5xx: only correlation id for support — never SQL / stack / params in the toast
            if (isServerErrorStatus(response.status)) {
                return {
                    title: contextInfo.title,
                    message: contextInfo.message,
                    detailParts: streamIdValue ? { streamId: streamIdValue } : undefined,
                };
            }

            return {
                title: contextInfo.title,
                message: contextInfo.message,
                detailParts: omitTechnicalDetails
                    ? supplemental
                        ? { supplemental }
                        : undefined
                    : {
                          httpStatus: response.status,
                          requestLabel,
                          streamId: streamIdValue,
                          supplemental,
                      },
            };
        }

        if (
            error !== null &&
            typeof error === 'object' &&
            'status' in error &&
            'message' in error &&
            typeof (error as { status: unknown }).status === 'number' &&
            typeof (error as { message: unknown }).message === 'string'
        ) {
            const customError = error as {
                status: number;
                message: string;
                details?: string;
                config?: AxiosErrorWithConfig['config'];
            };
            const contextInfo = getContextualErrorInfo(customError.status, customError.message);
            const supplemental = toSafeSupplementalDetail(customError.status, customError.details);
            const omitTechnicalDetails = customError.status === 400 || customError.status === 412 || customError.status === 422;

            if (isServerErrorStatus(customError.status)) {
                return {
                    title: contextInfo.title,
                    message: contextInfo.message,
                };
            }

            return {
                title: contextInfo.title,
                message: contextInfo.message,
                detailParts: omitTechnicalDetails
                    ? supplemental
                        ? { supplemental }
                        : undefined
                    : {
                          httpStatus: customError.status,
                          requestLabel: getRequestLabel(customError.config),
                          supplemental,
                      },
            };
        }

        if (error instanceof Error) {
            const axiosError = axios.isAxiosError(error) ? error : null;
            const message = isUnsafeInternalErrorMessage(error.message) ? safeGenericErrorMessage() : error.message;
            return {
                title: tApiError('requestErrorTitle'),
                message,
                detailParts: {
                    note: error.name !== 'Error' ? error.name : undefined,
                    requestLabel: getRequestLabel(axiosError?.config),
                },
            };
        }

        if (typeof error === 'string') {
            return {
                title: tApiError('requestErrorTitle'),
                message: isUnsafeInternalErrorMessage(error) ? safeGenericErrorMessage() : error,
            };
        }

        return {
            title: tApiError('unknownErrorTitle'),
            message: tApiError('unexpectedOccurred'),
            detailParts: {
                supplemental: tApiError('contactSupport'),
            },
        };
    }

    private generateErrorKey(errorInfo: { title: string; message: string }, serviceName: string): string {
        return `${serviceName}:${errorInfo.title}:${errorInfo.message}`;
    }

    private shouldShowError(errorKey: string, message: string): boolean {
        const now = Date.now();
        const existingError = this.recentErrors.get(errorKey);

        this.cleanupOldErrors(now);

        if (this.recentErrors.size >= this.MAX_CONCURRENT_ERRORS && !existingError) {
            if (import.meta.env.DEV) {
                console.warn('Too many concurrent errors, skipping toast for:', message);
            }
            return false;
        }

        if (existingError && now - existingError.timestamp < this.ERROR_DEBOUNCE_TIME) {
            return false;
        }

        return true;
    }

    private showErrorToast(errorInfo: ExtractedApiError, serviceName: string): void {
        if (errorInfo.actionButton) {
            const { label, url } = errorInfo.actionButton;
            let toastId: string | number;
            toastId = toast.error(errorInfo.title, {
                description: (
                    <div className="flex flex-col gap-3">
                        <span>{errorInfo.message}</span>
                        <button
                            onClick={() => {
                                window.open(url, '_blank');
                                toast.dismiss(toastId);
                            }}
                            className="self-start bg-white/10 text-white border border-white/40 rounded-md px-3 py-1 text-xs font-medium cursor-pointer hover:bg-white/25 transition-colors duration-150"
                        >
                            {label}
                        </button>
                    </div>
                ),
                duration: errorInfo.duration ?? 6000,
                position: 'top-right',
                style: ERROR_TOAST_STYLE,
                className: 'api-error-toast',
                icon: false,
                closeButton: true,
                classNames: { closeButton: '!bg-white !border-transparent [&>svg]:!text-destructive' },
            });
            return;
        }

        const formattedDescription = this.formatErrorDescription(errorInfo.message, errorInfo.detailParts);

        toast.error(`${serviceName}: ${errorInfo.title}`, {
            description: formattedDescription,
            duration: errorInfo.duration ?? 6000,
            position: 'top-right',
            style: ERROR_TOAST_STYLE,
            className: 'api-error-toast',
            icon: false,
        });
    }

    private formatErrorDescription(message: string, detailParts?: ApiErrorDetailParts): string {
        if (!detailParts) {
            return message;
        }

        const formattedLines: string[] = [];

        if (detailParts.note) {
            formattedLines.push(`💬 ${detailParts.note}`);
        }
        if (detailParts.httpStatus != null) {
            formattedLines.push(`📊 ${tApiError('detailHttp', { status: detailParts.httpStatus })}`);
        }
        if (detailParts.requestLabel) {
            formattedLines.push(`🔗 ${tApiError('detailUrl', { url: detailParts.requestLabel })}`);
        }
        if (detailParts.errorCode) {
            formattedLines.push(`⚠️ ${tApiError('detailErrorCode', { code: detailParts.errorCode })}`);
        }
        if (detailParts.streamId) {
            formattedLines.push(`🔍 ${tApiError('detailStreamId', { id: detailParts.streamId })}`);
        }
        if (detailParts.supplemental) {
            formattedLines.push(`💬 ${detailParts.supplemental}`);
        }

        if (formattedLines.length === 0) {
            return message;
        }

        return `${message}\n\n${formattedLines.join('\n')}`;
    }

    private trackError(errorKey: string, message: string): void {
        this.recentErrors.set(errorKey, {
            id: errorKey,
            timestamp: Date.now(),
            message,
        });
    }

    private cleanupOldErrors(currentTime: number): void {
        const keysToDelete: string[] = [];

        for (const [key, errorInfo] of this.recentErrors.entries()) {
            if (currentTime - errorInfo.timestamp >= this.ERROR_DEBOUNCE_TIME) {
                keysToDelete.push(key);
            }
        }

        for (const key of keysToDelete) {
            this.recentErrors.delete(key);
        }
    }
}

export const apiErrorHandler = ApiErrorHandler.getInstance();

export const handleApiError = async (error: unknown, serviceName?: string) => {
    await apiErrorHandler.handleApiError(error, serviceName);
};

interface ErrorWithMessage {
    message?: string;
}

function innermostErrorMessage(error: unknown): string | undefined {
    const inner = getInnerMostError(error);
    if (typeof inner === 'string' && inner.trim().length > 0) {
        return inner.trim();
    }
    if (inner !== null && typeof inner === 'object' && 'message' in inner) {
        const { message } = inner as ErrorWithMessage;
        if (typeof message === 'string' && message.trim().length > 0) {
            return message.trim();
        }
    }
    return undefined;
}

/**
 * User-facing message from API errors (Axios `response.data` or `Error.message`).
 * Aligns with {@link ApiErrorHandler} extraction — use in catch blocks when handling errors locally (e.g. with `skipGlobalErrorToast`).
 * Never returns raw 5xx / SQL / stack payloads.
 */
export function getApiErrorUserMessage(error: unknown): string | undefined {
    if (axios.isAxiosError(error) && error.response) {
        const data = parseApiErrorResponseData(error.response.data);
        const raw = getApiResponseMessage(data, error.response.statusText);
        return toUserFacingErrorMessage(error.response.status, raw);
    }
    if (error !== null && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: unknown; statusText?: string; status?: number } }).response;
        const data = parseApiErrorResponseData(response?.data);
        const message = getApiResponseMessage(data, response?.statusText);
        if (message !== tApiError('unknownOccurred')) {
            return toUserFacingErrorMessage(response?.status, message);
        }
    }
    if (error instanceof Error && error.message.length > 0) {
        return isUnsafeInternalErrorMessage(error.message) ? safeGenericErrorMessage() : error.message;
    }
    return undefined;
}

/**
 * Cockpit `maybeShowAxiosError` parity: innermost assert/API `message` when it is safe for users.
 * Unlike {@link getApiErrorUserMessage}, a 5xx status does not force a generic server toast if the
 * payload is a short assert (e.g. document-generation "Floor Plan document not found").
 * SQL / stack dumps still map to the sanitized generic copy.
 */
export function getApiErrorInnermostUserMessage(error: unknown): string | undefined {
    const inner = innermostErrorMessage(error);
    if (inner && !isUnsafeInternalErrorMessage(inner)) {
        return inner;
    }
    return getApiErrorUserMessage(error);
}
